import { useId, useState } from "react";
import { ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface MultiSelectOption {
  key: string;
  text: string;
}

/**
 * Mehrfachauswahl als Popover mit Kontrollkästchen — ersetzt die
 * Fluent-Dropdown-Mehrfachauswahl. Der Auswahlzustand bleibt eine reine
 * Schlüsselliste, wie im Bestand.
 */
export default function MultiSelect(properties: {
  label: string;
  placeholder: string;
  emptyLabel: string;
  options: MultiSelectOption[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
  /** Breite des Felds; Standard wie in den Filterleisten. */
  widthClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const labelId = useId();

  const toggleOption = (key: string) => {
    properties.onChange(
      properties.selectedKeys.includes(key)
        ? properties.selectedKeys.filter((candidate) => candidate !== key)
        : [...properties.selectedKeys, key],
    );
  };

  const triggerText = properties.selectedKeys.length
    ? properties.options
        .filter((option) => properties.selectedKeys.includes(option.key))
        .map((option) => option.text)
        .join(", ") || properties.placeholder
    : properties.placeholder;

  return (
    <div className={`flex ${properties.widthClassName || "w-[200px]"} max-w-full flex-col gap-1.5`}>
      <Label id={labelId}>{properties.label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-labelledby={labelId}
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{triggerText}</span>
            <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>{properties.emptyLabel}</CommandEmpty>
              <CommandGroup>
                {properties.options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={option.text}
                    className="gap-2"
                    onSelect={() => toggleOption(option.key)}
                  >
                    <Checkbox
                      checked={properties.selectedKeys.includes(option.key)}
                      tabIndex={-1}
                      className="pointer-events-none"
                    />
                    <span className="min-w-0 break-words">{option.text}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
